import { useState, useCallback } from 'react';
import supabase from '../supabase';

export const useOptimizedQueries = () => {
  const [cache, setCache] = useState(new Map());

  // Función para obtener datos básicos de clientes con cache
  const getClientsBasicData = useCallback(async () => {
    const cacheKey = 'clients_basic';
    
    // Verificar cache
    if (cache.has(cacheKey)) {
      const cachedData = cache.get(cacheKey);
      const now = Date.now();
      // Cache válido por 5 minutos
      if (now - cachedData.timestamp < 5 * 60 * 1000) {
        return cachedData.data;
      }
    }

    try {
      const { data, error } = await supabase
        .from("clients")
        .select(`
          *,
          orders!inner(
            orderId,
            status,
            subTotal,
            iva105,
            iva210,
            createdAt
          )
        `);

      if (error) throw error;

      // Guardar en cache
      setCache(prev => new Map(prev).set(cacheKey, {
        data,
        timestamp: Date.now()
      }));

      return data;
    } catch (error) {
      console.error('Error obteniendo datos básicos de clientes:', error);
      throw error;
    }
  }, [cache]);

  // Función para obtener detalles de órdenes con cache
  const getOrderDetails = useCallback(async (orderId) => {
    const cacheKey = `order_details_${orderId}`;
    
    // Verificar cache
    if (cache.has(cacheKey)) {
      const cachedData = cache.get(cacheKey);
      const now = Date.now();
      // Cache válido por 10 minutos
      if (now - cachedData.timestamp < 10 * 60 * 1000) {
        return cachedData.data;
      }
    }

    try {
      const { data, error } = await supabase
        .from("orderDetails")
        .select("*")
        .eq("orderId", orderId);

      if (error) throw error;

      // Guardar en cache
      setCache(prev => new Map(prev).set(cacheKey, {
        data,
        timestamp: Date.now()
      }));

      return data;
    } catch (error) {
      console.error(`Error obteniendo detalles para orden ${orderId}:`, error);
      throw error;
    }
  }, [cache]);

  // Función para obtener archivos con cache
  const getFilesOptimized = useCallback(async (bucketName, orderId) => {
    const cacheKey = `files_${bucketName}_${orderId}`;
    
    // Verificar cache
    if (cache.has(cacheKey)) {
      const cachedData = cache.get(cacheKey);
      const now = Date.now();
      // Cache válido por 15 minutos
      if (now - cachedData.timestamp < 15 * 60 * 1000) {
        return cachedData.data;
      }
    }

    try {
      // Guardas defensivas para evitar llamadas a /storage/.../undefined
      if (!bucketName || !orderId) {
        return [];
      }

      // Función de ayuda: reintentos exponenciales para conexiones cerradas/transitorias
      const withRetries = async (fn, retries = 3, baseDelayMs = 300) => {
        let lastErr;
        for (let i = 0; i < retries; i++) {
          try {
            return await fn();
          } catch (err) {
            lastErr = err;
            // Si es el último intento, relanzamos
            if (i === retries - 1) break;
            await new Promise(r => setTimeout(r, baseDelayMs * Math.pow(2, i)));
          }
        }
        throw lastErr;
      };

      const data = await withRetries(async () => {
        const { data, error } = await supabase.storage
          .from(bucketName)
          .list(`${orderId}/`);
        if (error) throw error;
        return data;
      });

      const files = await Promise.all(
        data.map(async (file) => {
          const res = await withRetries(async () => {
            return supabase.storage
              .from(bucketName)
              .getPublicUrl(`${orderId}/${file.name}`);
          });
          return res.data.publicUrl;
        })
      );

      // Guardar en cache
      setCache(prev => new Map(prev).set(cacheKey, {
        data: files,
        timestamp: Date.now()
      }));

      return files;
    } catch (error) {
      console.error(`Error obteniendo archivos para orden ${orderId}:`, error);
      return [];
    }
  }, [cache]);

  // Función para limpiar cache
  const clearCache = useCallback(() => {
    setCache(new Map());
  }, []);

  // Función para limpiar cache específico
  const clearCacheForOrder = useCallback((orderId) => {
    setCache(prev => {
      const newCache = new Map(prev);
      newCache.delete(`order_details_${orderId}`);
      newCache.delete(`files_${orderId}`);
      return newCache;
    });
  }, []);

  return {
    getClientsBasicData,
    getOrderDetails,
    getFilesOptimized,
    clearCache,
    clearCacheForOrder
  };
};

